import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "../../../supabase/supabase";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpDown,
  Check,
  Clock,
  Copy,
  Loader2,
  RotateCcw,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PromptVersion, PromptWithVersions, PromptDiff } from "@/types/prompt";
import { Separator } from "@/components/ui/separator";
import { DiffViewer } from "@/components/ui/diff-viewer";

export default function PromptHistory() {
  const { id } = useParams<{ id: string }>();
  const [prompt, setPrompt] = useState<PromptWithVersions | null>(null);
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<PromptVersion | null>(
    null,
  );
  const [compareVersion, setCompareVersion] = useState<PromptVersion | null>(
    null,
  );
  const [diff, setDiff] = useState<PromptDiff | null>(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchPromptHistory(id);
    }
  }, [id]);

  const fetchPromptHistory = async (promptId: string) => {
    try {
      setLoading(true);

      // Fetch the prompt
      const { data: promptData, error: promptError } = await supabase
        .from("prompts")
        .select("*")
        .eq("id", promptId)
        .single();

      if (promptError) throw promptError;

      // Fetch all versions for this prompt
      const { data: versionsData, error: versionsError } = await supabase
        .from("prompt_versions")
        .select("*")
        .eq("prompt_id", promptId)
        .order("version_number", { ascending: false });

      if (versionsError) throw versionsError;

      const promptWithVersions: PromptWithVersions = {
        ...promptData,
        versions: versionsData || [],
        latest_version:
          versionsData && versionsData.length > 0 ? versionsData[0] : undefined,
      };

      setPrompt(promptWithVersions);
      setVersions(versionsData || []);

      // Set the latest version as selected by default
      if (versionsData && versionsData.length > 0) {
        setSelectedVersion(versionsData[0]);

        // If there's more than one version, set the second latest as compare version
        if (versionsData.length > 1) {
          setCompareVersion(versionsData[1]);

          // Generate diff between the two versions
          setDiff({
            oldContent: versionsData[1].content,
            newContent: versionsData[0].content,
            oldVersionNumber: versionsData[1].version_number,
            newVersionNumber: versionsData[0].version_number,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching prompt history:", error);
      toast({
        title: "Error",
        description: "Failed to load prompt history. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVersionSelect = (version: PromptVersion) => {
    setSelectedVersion(version);

    // If compare version is not set or is the same as the selected version, find another version
    if (!compareVersion || compareVersion.id === version.id) {
      const otherVersion = versions.find((v) => v.id !== version.id);
      if (otherVersion) {
        setCompareVersion(otherVersion);

        // Generate diff between the two versions
        const oldVer =
          otherVersion.version_number < version.version_number
            ? otherVersion
            : version;
        const newVer =
          otherVersion.version_number < version.version_number
            ? version
            : otherVersion;

        setDiff({
          oldContent: oldVer.content,
          newContent: newVer.content,
          oldVersionNumber: oldVer.version_number,
          newVersionNumber: newVer.version_number,
        });
      } else {
        setCompareVersion(null);
        setDiff(null);
      }
    } else {
      // Generate diff between the selected and compare versions
      const oldVer =
        compareVersion.version_number < version.version_number
          ? compareVersion
          : version;
      const newVer =
        compareVersion.version_number < version.version_number
          ? version
          : compareVersion;

      setDiff({
        oldContent: oldVer.content,
        newContent: newVer.content,
        oldVersionNumber: oldVer.version_number,
        newVersionNumber: newVer.version_number,
      });
    }
  };

  const handleCompareVersionSelect = (version: PromptVersion) => {
    setCompareVersion(version);

    if (selectedVersion) {
      // Generate diff between the selected and compare versions
      const oldVer =
        version.version_number < selectedVersion.version_number
          ? version
          : selectedVersion;
      const newVer =
        version.version_number < selectedVersion.version_number
          ? selectedVersion
          : version;

      setDiff({
        oldContent: oldVer.content,
        newContent: newVer.content,
        oldVersionNumber: oldVer.version_number,
        newVersionNumber: newVer.version_number,
      });
    }
  };

  const restoreVersion = async () => {
    if (!selectedVersion || !prompt) return;

    try {
      setRestoring(true);

      // Get the current user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Get the latest version number
      const latestVersionNumber =
        versions.length > 0 ? versions[0].version_number : 0;

      // Create a new version with the content from the selected version
      const { error: versionError } = await supabase
        .from("prompt_versions")
        .insert({
          prompt_id: prompt.id,
          content: selectedVersion.content,
          version_number: latestVersionNumber + 1,
          created_by: user.id,
        });

      if (versionError) throw versionError;

      toast({
        title: "Success",
        description: `Restored version ${selectedVersion.version_number} as the latest version.`,
      });

      // Refresh the history
      fetchPromptHistory(prompt.id);
    } catch (error) {
      console.error("Error restoring version:", error);
      toast({
        title: "Error",
        description: "Failed to restore version. Please try again.",
        variant: "destructive",
      });
    } finally {
      setRestoring(false);
    }
  };

  const copyToClipboard = (content: string) => {
    navigator.clipboard.writeText(content);
    toast({
      title: "Copied",
      description: "Prompt version copied to clipboard.",
    });
  };

  // Enhanced diff visualization using react-diff-viewer
  const renderDiff = () => {
    if (!diff) return null;

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-medium">Comparing Changes</h3>
          <div className="flex items-center text-sm text-muted-foreground">
            <span>Version {diff.oldVersionNumber}</span>
            <ArrowUpDown className="h-4 w-4 mx-2" />
            <span>Version {diff.newVersionNumber}</span>
          </div>
        </div>
        <div className="border rounded-md overflow-hidden">
          <DiffViewer
            oldText={diff.oldContent}
            newText={diff.newContent}
            splitView={false}
            leftTitle={`Version ${diff.oldVersionNumber}`}
            rightTitle={`Version ${diff.newVersionNumber}`}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mb-4" />
            <p>Loading prompt history...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!prompt) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground">Prompt not found.</p>
            <Button className="mt-4" onClick={() => navigate("/prompt-engine")}>
              Back to Prompts
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center mb-2">
          <Button
            variant="ghost"
            size="sm"
            className="mr-2 -ml-2"
            onClick={() => navigate(`/prompt-engine/prompt/${prompt.id}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Prompt
          </Button>
        </div>
        <CardTitle className="text-xl">Version History</CardTitle>
        <CardDescription>
          View and compare different versions of "{prompt.title}"
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-1 border rounded-md overflow-hidden">
            <div className="bg-muted p-3">
              <h3 className="text-sm font-medium">Versions</h3>
            </div>
            <ScrollArea className="h-[400px]">
              <div className="p-2">
                {versions.map((version) => (
                  <div
                    key={version.id}
                    className={`flex items-center justify-between p-2 rounded-md cursor-pointer ${selectedVersion?.id === version.id ? "bg-primary/10" : "hover:bg-muted"}`}
                    onClick={() => handleVersionSelect(version)}
                  >
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                      <div>
                        <div className="text-sm font-medium">
                          Version {version.version_number}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(version.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    {selectedVersion?.id === version.id && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
          <div className="col-span-2 space-y-4">
            {selectedVersion && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-medium">
                    Version {selectedVersion.version_number}
                  </h3>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(selectedVersion.content)}
                    >
                      <Copy className="h-4 w-4 mr-1" /> Copy
                    </Button>
                    {selectedVersion.version_number !==
                      versions[0]?.version_number && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={restoreVersion}
                        disabled={restoring}
                      >
                        {restoring ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />{" "}
                            Restoring...
                          </>
                        ) : (
                          <>
                            <RotateCcw className="h-4 w-4 mr-1" /> Restore
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                <ScrollArea className="border rounded-md p-4 h-[200px]">
                  <pre className="whitespace-pre-wrap font-mono text-sm">
                    {selectedVersion.content}
                  </pre>
                </ScrollArea>

                {versions.length > 1 && (
                  <>
                    <Separator />
                    {renderDiff()}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button
          variant="outline"
          onClick={() => navigate(`/prompt-engine/prompt/${prompt.id}`)}
        >
          Back to Prompt
        </Button>
      </CardFooter>
    </Card>
  );
}
